import { db } from "../firebase.js";
import { doc, getDoc, setDoc, serverTimestamp, collection } from "firebase/firestore";
import { cacheUserProfile } from "./authState.js";

export const ORG_ROOT = "companies";
export const GROUP_ALL_STAFF = "grp_all_staff";
export const GROUP_INACTIVE_STAFF = "grp_inactive_staff";
export const GROUP_DELIVERY_STAFF = "grp_delivery_staff";
export const GROUP_WAREHOUSE_STAFF = "grp_warehouse_staff";

export const safeTrimEmail = (value) => (value || "").trim().toLowerCase();
export const safeTrimName = (value) => (value || "").trim();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function generateCompanyId() {
  return doc(collection(db, ORG_ROOT)).id;
}

export async function bootstrapOwnerWorkspace({ companyId, ownerUid, ownerEmail }) {
  if (!companyId) throw new Error("bootstrapOwnerWorkspace: missing companyId");
  if (!ownerUid) throw new Error("bootstrapOwnerWorkspace: missing ownerUid");
  if (companyId === ownerUid) {
    throw new Error("Invalid companyId: companyId must NOT equal owner uid.");
  }

  const companyRef = doc(db, ORG_ROOT, companyId);
  const companySnap = await getDoc(companyRef);

  const companyPayload = {
    companyId,
    name: "New Company",
    ownerUid,
    ownerEmail: ownerEmail || null,
    visibility: "groups",
    staffGroupId: GROUP_ALL_STAFF,
    createdAt: companySnap.exists() ? companySnap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
    createdBy: companySnap.exists() ? companySnap.data()?.createdBy || ownerUid : ownerUid,
    updatedAt: serverTimestamp(),
    updatedBy: ownerUid,
  };

  await setDoc(companyRef, companyPayload, { merge: true });

  const allStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_ALL_STAFF);
  const inactiveStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_INACTIVE_STAFF);
  const deliveryStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_DELIVERY_STAFF);
  const warehouseStaffRef = doc(db, ORG_ROOT, companyId, "groups", GROUP_WAREHOUSE_STAFF);

  const [allStaffSnap, inactiveStaffSnap, deliveryStaffSnap, warehouseStaffSnap] = await Promise.all([
    getDoc(allStaffRef),
    getDoc(inactiveStaffRef),
    getDoc(deliveryStaffRef),
    getDoc(warehouseStaffRef),
  ]);

  if (!allStaffSnap.exists()) {
    await setDoc(
      allStaffRef,
      {
        name: "All Staff",
        description: "Default group that includes all internal staff users.",
        isSystem: true,
        systemKey: "all_staff",
        createdAt: serverTimestamp(),
        createdBy: ownerUid,
        updatedAt: serverTimestamp(),
        updatedBy: ownerUid,
      },
      { merge: true }
    );
  } else {
    await setDoc(allStaffRef, { updatedAt: serverTimestamp(), updatedBy: ownerUid }, { merge: true });
  }

  if (!inactiveStaffSnap.exists()) {
    await setDoc(
      inactiveStaffRef,
      {
        name: "Inactive Staff",
        description: "System group for staff who are inactive.",
        isSystem: true,
        systemKey: "inactive_staff",
        createdAt: serverTimestamp(),
        createdBy: ownerUid,
        updatedAt: serverTimestamp(),
        updatedBy: ownerUid,
      },
      { merge: true }
    );
  } else {
    await setDoc(inactiveStaffRef, { updatedAt: serverTimestamp(), updatedBy: ownerUid }, { merge: true });
  }

  if (!deliveryStaffSnap.exists()) {
    await setDoc(
      deliveryStaffRef,
      {
        name: "Delivery Staff",
        description: "Admin-only operational group. Members can only move sample orders from Shipped to Delivered.",
        isSystem: true,
        systemKey: "delivery_staff",
        createdAt: serverTimestamp(),
        createdBy: ownerUid,
        updatedAt: serverTimestamp(),
        updatedBy: ownerUid,
      },
      { merge: true }
    );
  } else {
    await setDoc(deliveryStaffRef, { updatedAt: serverTimestamp(), updatedBy: ownerUid }, { merge: true });
  }

  if (!warehouseStaffSnap.exists()) {
    await setDoc(
      warehouseStaffRef,
      {
        name: "Warehouse Staff",
        description: "Admin-only operational group. Members can only move sample orders from Approved to Preparation or Shipped.",
        isSystem: true,
        systemKey: "warehouse_staff",
        createdAt: serverTimestamp(),
        createdBy: ownerUid,
        updatedAt: serverTimestamp(),
        updatedBy: ownerUid,
      },
      { merge: true }
    );
  } else {
    await setDoc(warehouseStaffRef, { updatedAt: serverTimestamp(), updatedBy: ownerUid }, { merge: true });
  }

  return { companyId };
}

export async function createOwnerProfileRecords({ user, fullName = "", phone = "" }) {
  if (!user?.uid) throw new Error("Cannot create owner profile without an authenticated user.");

  const trimmedEmail = safeTrimEmail(user.email);
  const trimmedFullName = safeTrimName(fullName || user.displayName || "");
  const trimmedPhone = (phone || "").trim();
  const companyId = generateCompanyId();

  const profile = {
    uid: user.uid,
    email: trimmedEmail,
    fullName: trimmedFullName,
    phone: trimmedPhone,
    role: "owner",
    companyId,
    groupIds: [GROUP_ALL_STAFF],
    setupCompleted: false,
    companySetupCompleted: false,
    subscriptionActive: false,
    planType: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, "users", user.uid), profile, { merge: true });
  await bootstrapOwnerWorkspace({
    companyId,
    ownerUid: user.uid,
    ownerEmail: trimmedEmail,
  });

  cacheUserProfile(profile);
  return profile;
}

export async function fetchAndCacheUserProfile(uid) {
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const profile = userSnap.data() || {};
    cacheUserProfile(profile);
    return profile;
  }

  return null;
}

export async function ensureOwnerProfileForAuthUser(user) {
  if (!user) return null;

  const userRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userRef);
  let profile;

  if (!userSnap.exists()) {
    const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;

    await sleep(500);
    const recheckSnap = await getDoc(userRef);

    if (recheckSnap.exists()) {
      profile = recheckSnap.data() || {};
      cacheUserProfile(profile);
      return profile;
    }

    if (!isNewUser) {
      throw new Error(
        "Your account exists but user profile is missing. Please contact support. DO NOT proceed as this will create duplicate data."
      );
    }

    return createOwnerProfileRecords({
      user,
      fullName: user.displayName || "",
      phone: "",
    });
  }

  profile = userSnap.data() || {};
  const patch = {};

  if (!profile.uid) patch.uid = user.uid;
  if (!profile.email && user.email) patch.email = user.email;
  if (!profile.fullName && user.displayName) patch.fullName = user.displayName;
  if (!profile.role) patch.role = "owner";

  if (!profile.companyId) {
    throw new Error(
      "Your account is missing company information. Please contact support to restore your data."
    );
  }

  let companyData = null;
  try {
    const companySnap = await getDoc(doc(db, ORG_ROOT, profile.companyId));
    if (companySnap.exists()) {
      companyData = companySnap.data() || {};
    }
  } catch (companyErr) {
    console.warn("[ownerProfile] Could not read company while healing profile:", companyErr);
  }

  const normalizedPlanType = (profile.planType || "").toString().toLowerCase();
  const hasCompletedSetup =
    profile.companySetupCompleted === true ||
    (!!profile.companyId &&
      (
        profile.setupCompleted === true ||
        (normalizedPlanType === "trial" && !!profile.trialEndsAt) ||
        (normalizedPlanType === "paid" && profile.subscriptionActive === true) ||
        companyData?.onboardingComplete === true
      ));

  if (hasCompletedSetup && !profile.companySetupCompleted) {
    patch.companySetupCompleted = true;
  }

  if (Object.keys(patch).length > 0) {
    patch.updatedAt = serverTimestamp();
    await setDoc(userRef, patch, { merge: true });
    profile = { ...profile, ...patch };
  }

  cacheUserProfile(profile);
  return profile;
}
